import type { GrooveState, MatchShape } from "./types";

export const GROOVE_MODE_MULTIPLIER = 3;
export const GROOVE_MODE_DURATION_SEC = 10;

export function grooveToMultiplier(groove: GrooveState): number {
  return groove.active ? GROOVE_MODE_MULTIPLIER : 1;
}

export interface ScoreInputs {
  tilesCleared: number;
  matchLen: number;
  chainIndex: number;
  shape: MatchShape;
  bonusPoints?: number;
  level?: number;
}

export interface ScoreOutputs {
  points: number;
}

const PASSIVE_MATCH_POINTS = {
  line3: 100,
  line4: 200,
  line5: 400,
  square: 200,
  lShape: 800,
  tShape: 1600
} as const;

const MATCH_GROOVE_BONUS_FRAC = {
  line3: 0.0035,
  line4: 0.0045,
  line5Plus: 0.006,
  square: 0.005,
  lShape: 0.006,
  tShape: 0.0065,
  composite: 0.007
} as const;

function shapeBonus(shape: MatchShape): number {
  if (shape === "SQUARE") return 1;
  if (shape === "L") return 1.25;
  if (shape === "T") return 1.35;
  if (shape === "COMPOSITE") return 1.4;
  return 1;
}

export function grooveBonusFracForMatch(shape: MatchShape, matchLen: number): number {
  if (shape === "LINE") {
    if (matchLen >= 5) return MATCH_GROOVE_BONUS_FRAC.line5Plus;
    if (matchLen >= 4) return MATCH_GROOVE_BONUS_FRAC.line4;
    return MATCH_GROOVE_BONUS_FRAC.line3;
  }
  if (shape === "SQUARE") return MATCH_GROOVE_BONUS_FRAC.square;
  if (shape === "L") return MATCH_GROOVE_BONUS_FRAC.lShape;
  if (shape === "T") return MATCH_GROOVE_BONUS_FRAC.tShape;
  if (shape === "COMPOSITE") return MATCH_GROOVE_BONUS_FRAC.composite;
  return MATCH_GROOVE_BONUS_FRAC.line3;
}

function lineMatchPoints(length: number): number {
  if (length >= 6) {
    return PASSIVE_MATCH_POINTS.line5 + (length - 5) * 200;
  }
  if (length >= 5) {
    return PASSIVE_MATCH_POINTS.line5;
  }
  if (length === 4) {
    return PASSIVE_MATCH_POINTS.line4;
  }
  return PASSIVE_MATCH_POINTS.line3;
}

function passiveMatchPoints(shape: MatchShape, matchLen: number, tilesCleared: number): number {
  if (shape === "SQUARE") return PASSIVE_MATCH_POINTS.square;
  if (shape === "L") return PASSIVE_MATCH_POINTS.lShape;
  if (shape === "T") return PASSIVE_MATCH_POINTS.tShape;
  if (shape === "COMPOSITE") return lineMatchPoints(Math.max(3, tilesCleared));
  return lineMatchPoints(Math.max(3, matchLen));
}

export function chainMultiplierForDepth(chainDepth: number): number {
  const depth = Math.max(0, Math.floor(chainDepth));
  return depth <= 1 ? 1 : 1 + depth * 0.1;
}

export function scorePassiveMatchEvent(groove: GrooveState, inputs: ScoreInputs): ScoreOutputs {
  groove.multiplier = grooveToMultiplier(groove);
  return {
    points: passiveMatchPoints(inputs.shape, inputs.matchLen, inputs.tilesCleared)
  };
}

export function scoreClearEvent(groove: GrooveState, inputs: ScoreInputs): ScoreOutputs {
  groove.multiplier = grooveToMultiplier(groove);
  const base = inputs.shape === "SQUARE" ? 50 : inputs.tilesCleared * 10;
  const sizeBonus = inputs.shape === "SQUARE" ? 1 : 1 + 0.25 * Math.max(0, inputs.matchLen - 3);
  const matchShapeBonus = shapeBonus(inputs.shape);
  const points = Math.round(base * sizeBonus * matchShapeBonus + (inputs.bonusPoints ?? 0));

  groove.multiplier = grooveToMultiplier(groove);
  return { points };
}
